import type { Reference } from '@medplum/fhirtypes'
import type { ExtendedComposition } from '@seen/core/models/compositions/composition'
import type { Order } from '@seen/core/models/orders/Order'
import type { StandardTask } from '@seen/core/models/tasks/standard/standardTask'
import { sortBy } from 'lodash-es'
import type { ReactNode } from 'react'
import {
  LabelChangeAction,
  type LabelClass,
} from '#src/components/activity/LabelChangeAction.tsx'

/*
 * Time window for grouping consecutive label changes by the same author.
 * Changes within this window are merged into a single activity entry.
 * Matches GROUPING_THRESHOLD_MS from useTimelineItems.ts.
 */
const LABEL_ACTIVITY_GROUPING_WINDOW_MS = 5 * 60 * 1000 // 5 minutes

export interface LabelChangeData {
  added: string[]
  removed: string[]
  LabelClass: LabelClass
}

/*
 * Activity entry for task-related label changes.
 */
export interface TaskLabelActivityEntry {
  action: ReactNode
  task: StandardTask
  key?: string
  labelChange?: LabelChangeData
}

/*
 * Activity entry for order-related label changes.
 */
export interface OrderLabelActivityEntry {
  action: ReactNode
  order: Order
  key?: string
  labelChange?: LabelChangeData
}

/*
 * Activity entry for note (Composition) label changes.
 */
export interface CompositionLabelActivityEntry {
  action: ReactNode
  composition: ExtendedComposition
  key?: string
  labelChange?: LabelChangeData
}

interface GetTaskEntryMetaResult {
  timestamp: Date | string
  author: Reference
}

/*
 * Extracts timestamp and author from a task activity entry.
 */
function getTaskEntryMeta(
  entry: TaskLabelActivityEntry
): GetTaskEntryMetaResult {
  return {
    timestamp: entry.task.lastUpdated,
    author: entry.task.lastUpdatedBy,
  }
}

interface GetOrderEntryMetaResult {
  timestamp: Date | string
  author: Reference
}

/*
 * Extracts timestamp and author from an order activity entry.
 */
function getOrderEntryMeta(
  entry: OrderLabelActivityEntry
): GetOrderEntryMetaResult {
  return {
    timestamp: entry.order.lastUpdated,
    author: entry.order.lastUpdatedBy,
  }
}

/*
 * Groups consecutive task label-change entries from the same author
 * within the time window. Applies net-zero cancellation.
 *
 * Non-label entries (those without labelChange data) pass through unchanged.
 */
export function groupTaskLabelActivityEntries(
  entries: TaskLabelActivityEntry[],
  windowMs: number = LABEL_ACTIVITY_GROUPING_WINDOW_MS
): TaskLabelActivityEntry[] {
  return groupLabelActivityEntriesGeneric(entries, getTaskEntryMeta, windowMs)
}

/*
 * Groups consecutive order label-change entries from the same author
 * within the time window. Applies net-zero cancellation.
 *
 * Non-label entries (those without labelChange data) pass through unchanged.
 */
export function groupOrderLabelActivityEntries(
  entries: OrderLabelActivityEntry[],
  windowMs: number = LABEL_ACTIVITY_GROUPING_WINDOW_MS
): OrderLabelActivityEntry[] {
  return groupLabelActivityEntriesGeneric(entries, getOrderEntryMeta, windowMs)
}

interface GetCompositionEntryMetaResult {
  timestamp: Date | string
  author: Reference
}

/*
 * Extracts timestamp and author from a note (Composition) activity entry.
 */
function getCompositionEntryMeta(
  entry: CompositionLabelActivityEntry
): GetCompositionEntryMetaResult {
  return {
    timestamp: entry.composition.lastUpdated,
    author: entry.composition.lastUpdatedBy,
  }
}

/*
 * Groups a note's label-change entries from the same author within the time
 * window, with net-zero cancellation. Non-label entries (content edits and
 * category changes) are set aside first so they can't split a label run: a
 * category change or autosave between a label add and its removal must still let
 * the two net-cancel. Merged entries are re-sorted chronologically so setting the
 * non-label entries aside doesn't leave the result out of order.
 */
export function groupCompositionLabelActivityEntries(
  entries: CompositionLabelActivityEntry[],
  windowMs: number = LABEL_ACTIVITY_GROUPING_WINDOW_MS
): CompositionLabelActivityEntry[] {
  const labelEntries = entries.filter((entry) => entry.labelChange)
  const otherEntries = entries.filter((entry) => !entry.labelChange)
  const grouped = groupLabelActivityEntriesGeneric(
    labelEntries,
    getCompositionEntryMeta,
    windowMs
  )
  return sortBy(
    [...grouped, ...otherEntries],
    (entry) => entry.composition.lastUpdated
  )
}

/*
 * Generic grouping function that works with any entry type.
 */
function groupLabelActivityEntriesGeneric<
  T extends { labelChange?: LabelChangeData },
>(
  entries: T[],
  getMeta: (entry: T) => { timestamp: Date | string; author: Reference },
  windowMs: number
): T[] {
  if (entries.length === 0) {
    return entries
  }

  const result: T[] = []
  let i = 0

  while (i < entries.length) {
    const current = entries[i]

    // Non-label entries pass through unchanged
    if (!current.labelChange) {
      result.push(current)
      i++
      continue
    }

    const currentMeta = getMeta(current)

    // Find all consecutive label entries from same author within window
    const group: T[] = [current]
    let j = i + 1
    let lastTime = new Date(currentMeta.timestamp).getTime()

    while (j < entries.length) {
      const next = entries[j]

      // Stop if not a label entry
      if (!next.labelChange) {
        break
      }

      const nextMeta = getMeta(next)

      // Stop if different author
      if (nextMeta.author.reference !== currentMeta.author.reference) {
        break
      }

      // Stop if different LabelClass
      if (next.labelChange.LabelClass !== current.labelChange.LabelClass) {
        break
      }

      // Stop if outside time window (compared to most recent entry, not first)
      const nextTime = new Date(nextMeta.timestamp).getTime()
      if (Math.abs(nextTime - lastTime) > windowMs) {
        break
      }

      group.push(next)
      lastTime = nextTime
      j++
    }

    // If only one entry in group, no merging needed
    if (group.length === 1) {
      result.push(current)
      i++
      continue
    }

    // Merge all label changes in the group
    const mergedAdded = new Set<string>()
    const mergedRemoved = new Set<string>()

    for (const entry of group) {
      if (!entry.labelChange) {
        continue
      }

      for (const label of entry.labelChange.added) {
        // If previously removed, cancel out
        if (mergedRemoved.has(label)) {
          mergedRemoved.delete(label)
        } else {
          mergedAdded.add(label)
        }
      }

      for (const label of entry.labelChange.removed) {
        // If previously added, cancel out
        if (mergedAdded.has(label)) {
          mergedAdded.delete(label)
        } else {
          mergedRemoved.add(label)
        }
      }
    }

    const added = Array.from(mergedAdded)
    const removed = Array.from(mergedRemoved)

    // If net-zero (all changes cancelled out), skip this group entirely
    if (added.length === 0 && removed.length === 0) {
      i = j
      continue
    }

    // Use the latest entry in the group as the base, update its action
    const latestEntry = group[group.length - 1]
    const LabelClass = current.labelChange.LabelClass

    const mergedEntry: T = {
      ...latestEntry,
      action: (
        <LabelChangeAction
          added={added}
          removed={removed}
          LabelClass={LabelClass}
        />
      ),
      labelChange: {
        added,
        removed,
        LabelClass,
      },
    }

    result.push(mergedEntry)
    i = j
  }

  return result
}
